import { z } from "zod";
import { workScopeStatusOptions, interiorWallMaterialsOptions, whatTypeOfFlooringOptions } from "../../constants/options";
import { multiSelectSchema, selectSchema, textSchema, numberSchema, claimedBySchema } from "../../../../shared/lib/schemas/common.schema";
import { makeOptional } from "../../../../shared/lib/helpers/schema.helpers";
import { completionDateSchema } from "./completion-date.schema";


export const muckGutInfoFormSchema = z.object({
    muckGutNeeded: makeOptional(selectSchema(workScopeStatusOptions)),
    muckGutNeededDateCompleted: completionDateSchema,
    heightOfWallRemoval: makeOptional(numberSchema, 'number'),
    ceilingRemoved: makeOptional(z.string()),
    claimedBy: claimedBySchema,
    description: makeOptional(textSchema("lg")),
    goodwillMuckGutNeededOn: makeOptional(z.boolean()),
    goodwillMuckGutNeeded: makeOptional(selectSchema(workScopeStatusOptions)),
    goodwillMuckGutNeededClaimedBy: claimedBySchema.optional(),
})

export const moldRemediationInfoFormSchema = z.object({
    moldRemediation: makeOptional(selectSchema(workScopeStatusOptions)),
    moldRemediationDateCompleted: completionDateSchema,
    highestMoistureLevel: makeOptional(z.number(), 'number'),
    claimedBy: claimedBySchema,
    description: makeOptional(textSchema("lg")),
    goodwillMoldRemediationOn: makeOptional(z.boolean()),
    goodwillMoldRemediation: makeOptional(selectSchema(workScopeStatusOptions)),
    goodwillMoldRemediationClaimedBy: claimedBySchema.optional(),
})

export const rebuildPrepInfoFormSchema = z.object({
    rebuildPrep: makeOptional(selectSchema(workScopeStatusOptions)),
    rebuildPrepDateCompleted: completionDateSchema,
    claimedBy: claimedBySchema,
    description: makeOptional(textSchema("lg")),
    goodwillRebuildPrepOn: makeOptional(z.boolean()),
    goodwillRebuildPrep: makeOptional(selectSchema(workScopeStatusOptions)),
    goodwillRebuildPrepClaimedBy: claimedBySchema.optional(),
})

export const roughCarpentryInfoFormSchema = z.object({
    roughCarpentry: makeOptional(selectSchema(workScopeStatusOptions)),
    roughCarpentryDateCompleted: completionDateSchema,
    needsWallsRepaired: makeOptional(z.string()),
    needsSubfloorRepair: makeOptional(z.string()),
    needsWindows: makeOptional(z.string()),
    numberOfWindows: makeOptional(z.number(), 'number'),
    claimedBy: claimedBySchema,
    description: makeOptional(textSchema("lg")),
    goodwillRoughCarpentryOn: makeOptional(z.boolean()),
    goodwillRoughCarpentry: makeOptional(selectSchema(workScopeStatusOptions)),
    goodwillRoughCarpentryClaimedBy: claimedBySchema.optional(),
})

export const electricalInfoFormSchema = z.object({
    electrical: makeOptional(selectSchema(workScopeStatusOptions)),
    electricalDateCompleted: completionDateSchema,
    claimedBy: claimedBySchema,
    electricalRoughIn: makeOptional(textSchema()),
    electricalRoughInDateCompleted: completionDateSchema,
    electricalRoughInClaimedBy: claimedBySchema,
    description: makeOptional(textSchema("lg")),
    goodwillElectricalRoughInOn: makeOptional(z.boolean()),
    goodwillElectricalRoughIn: makeOptional(selectSchema(workScopeStatusOptions)),
    goodwillElectricalRoughInClaimedBy: claimedBySchema.optional(),
    goodwillElectricalOn: makeOptional(z.boolean()),
    goodwillElectrical: makeOptional(selectSchema(workScopeStatusOptions)),
    goodwillElectricalClaimedBy: claimedBySchema.optional(),
})

export const hvacInfoFormSchema = z.object({
    hvac: makeOptional(selectSchema(workScopeStatusOptions)),
    hvacDateCompleted: completionDateSchema,
    claimedBy: claimedBySchema,
    mechanicalRoughIn: makeOptional(textSchema()),
    mechanicalRoughInDateCompleted: completionDateSchema,
    mechanicalRoughInClaimedBy: claimedBySchema,
    description: makeOptional(textSchema("lg")),
    goodwillMechanicalRoughInOn: makeOptional(z.boolean()),
    goodwillMechanicalRoughIn: makeOptional(selectSchema(workScopeStatusOptions)),
    goodwillMechanicalRoughInClaimedBy: claimedBySchema.optional(),
    goodwillHvacOn: makeOptional(z.boolean()),
    goodwillHvac: makeOptional(selectSchema(workScopeStatusOptions)),
    goodwillHvacClaimedBy: claimedBySchema.optional(),
})

export const plumbingInfoFormSchema = z.object({
    plumbing: makeOptional(selectSchema(workScopeStatusOptions)),
    plumbingDateCompleted: completionDateSchema,
    claimedBy: claimedBySchema,
    plumbingRoughIn: makeOptional(textSchema()),
    plumbingRoughInDateCompleted: completionDateSchema,
    plumbingRoughInClaimedBy: claimedBySchema,
    description: makeOptional(textSchema("lg")),
    goodwillPlumbingRoughInOn: makeOptional(z.boolean()),
    goodwillPlumbingRoughIn: makeOptional(selectSchema(workScopeStatusOptions)),
    goodwillPlumbingRoughInClaimedBy: claimedBySchema.optional(),
    goodwillPlumbingOn: makeOptional(z.boolean()),
    goodwillPlumbing: makeOptional(selectSchema(workScopeStatusOptions)),
    goodwillPlumbingClaimedBy: claimedBySchema.optional(),
})

export const insulationInstallInfoFormSchema = z.object({
    insulationInstall: makeOptional(selectSchema(workScopeStatusOptions)),
    insulationInstallDateCompleted: completionDateSchema,
    blanketWallSquareFeet: makeOptional(z.number(), 'number'),
    rigidWallSquareFeet: makeOptional(z.number(), 'number'),
    ceilingInsulationSquareFeet: makeOptional(z.number(), 'number'),
    claimedBy: claimedBySchema,
    description: makeOptional(textSchema("lg")),
    goodwillInsulationInstallOn: makeOptional(z.boolean()),
    goodwillInsulationInstall: makeOptional(selectSchema(workScopeStatusOptions)),
    goodwillInsulationInstallClaimedBy: claimedBySchema.optional(),
})

export const drywallHangingInfoFormSchema = z.object({
    drywallHanging: makeOptional(selectSchema(workScopeStatusOptions)),
    drywallHangingDateCompleted: completionDateSchema,
    interiorWallMaterial: makeOptional(multiSelectSchema(interiorWallMaterialsOptions)),
    hasTexturing: makeOptional(z.enum(["yes", "no"])),
    totalSquareFeet: makeOptional(z.number(), 'number'),
    claimedBy: claimedBySchema,
    description: makeOptional(textSchema("lg")),
    goodwillDrywallHangingOn: makeOptional(z.boolean()),
    goodwillDrywallHanging: makeOptional(selectSchema(workScopeStatusOptions)),
    goodwillDrywallHangingClaimedBy: claimedBySchema.optional(),
})

export const mudAndTapeInfoFormSchema = z.object({
    mudAndTape: makeOptional(selectSchema(workScopeStatusOptions)),
    mudAndTapeDateCompleted: completionDateSchema,
    claimedBy: claimedBySchema,
    description: makeOptional(textSchema("lg")),
    goodwillMudAndTapeOn: makeOptional(z.boolean()),
    goodwillMudAndTape: makeOptional(selectSchema(workScopeStatusOptions)),
    goodwillMudAndTapeClaimedBy: claimedBySchema.optional(),
})

export const blockWallInfoFormSchema = z.object({
    blockWallRepair: makeOptional(selectSchema(workScopeStatusOptions)),
    blockWallRepairDateCompleted: completionDateSchema,
    claimedBy: claimedBySchema,
    description: makeOptional(textSchema("lg")),
    goodwillBlockWallRepairOn: makeOptional(z.boolean()),
    goodwillBlockWallRepair: makeOptional(selectSchema(workScopeStatusOptions)),
    goodwillBlockWallRepairClaimedBy: claimedBySchema.optional(),
})

export const stuccoInfoFormSchema = z.object({
    stucco: makeOptional(selectSchema(workScopeStatusOptions)),
    stuccoDateCompleted: completionDateSchema,
    claimedBy: claimedBySchema,
    description: makeOptional(textSchema("lg")),
    goodwillStuccoOn: makeOptional(z.boolean()),
    goodwillStucco: makeOptional(selectSchema(workScopeStatusOptions)),
    goodwillStuccoClaimedBy: claimedBySchema.optional(),
})

export const cabinetInstallInfoFormSchema = z.object({
    cabinetInstall: makeOptional(selectSchema(workScopeStatusOptions)),
    cabinetInstallDateCompleted: completionDateSchema,
    counterInstall: makeOptional(selectSchema(workScopeStatusOptions)),
    counterInstallDateCompleted: completionDateSchema,
    kitchenCabinetLowers: makeOptional(z.number(), 'number'),
    kitchenCabinetUppers: makeOptional(z.number(), 'number'),
    cabinetClaimedBy: claimedBySchema,
    counterClaimedBy: claimedBySchema,
    applianceInstall: makeOptional(textSchema()),
    applianceInstallDateCompleted: completionDateSchema,
    applianceClaimedBy: claimedBySchema,
    description: makeOptional(textSchema("lg")),
    goodwillCabinetInstallOn: makeOptional(z.boolean()),
    goodwillCabinetInstall: makeOptional(selectSchema(workScopeStatusOptions)),
    goodwillCabinetInstallClaimedBy: claimedBySchema.optional(),
    goodwillCounterInstallOn: makeOptional(z.boolean()),
    goodwillCounterInstall: makeOptional(selectSchema(workScopeStatusOptions)),
    goodwillCounterInstallClaimedBy: claimedBySchema.optional(),
    goodwillApplianceInstallOn: makeOptional(z.boolean()),
    goodwillApplianceInstall: makeOptional(selectSchema(workScopeStatusOptions)),
    goodwillApplianceInstallClaimedBy: claimedBySchema.optional(),
})

export const bathroomRebuildInfoFormSchema = z.object({
    bathroomRebuild: makeOptional(selectSchema(workScopeStatusOptions)),
    bathroomRebuildDateCompleted: completionDateSchema,
    claimedBy: claimedBySchema,
    bathroomVanities: makeOptional(z.number(), 'number'),
    description: makeOptional(textSchema("lg")),
    goodwillBathroomRebuildOn: makeOptional(z.boolean()),
    goodwillBathroomRebuild: makeOptional(selectSchema(workScopeStatusOptions)),
    goodwillBathroomRebuildClaimedBy: claimedBySchema.optional(),
})

export const crawlSpaceInfoFormSchema = z.object({
    crawlSpaceMuck: makeOptional(textSchema()),
    crawlSpaceMuckDateCompleted: completionDateSchema,
    description: makeOptional(textSchema("lg")),
    crawlSpaceInsulation: makeOptional(textSchema()),
    crawlSpaceInsulationDateCompleted: completionDateSchema,
    crawlSpaceMuckClaimedBy: claimedBySchema,
    crawlSpaceInsulationClaimedBy: claimedBySchema,
    crawlSpaceSquareFeet: makeOptional(z.number(), 'number'),
    goodwillCrawlSpaceInsulationOn: makeOptional(z.boolean()),
    goodwillCrawlSpaceInsulation: makeOptional(selectSchema(workScopeStatusOptions)),
    goodwillCrawlSpaceInsulationClaimedBy: claimedBySchema.optional(),
    goodwillCrawlSpaceMuckOn: makeOptional(z.boolean()),
    goodwillCrawlSpaceMuck: makeOptional(selectSchema(workScopeStatusOptions)),
    goodwillCrawlSpaceMuckClaimedBy: claimedBySchema.optional(),
})

export const interiorPaintInfoFormSchema = z.object({
    interiorPaint: makeOptional(selectSchema(workScopeStatusOptions)),
    interiorPaintDateCompleted: completionDateSchema,
    wallPaintSquareFeet: makeOptional(z.number(), 'number'),
    ceilingPaintSquareFeet: makeOptional(z.number(), 'number'),
    claimedBy: claimedBySchema,
    description: makeOptional(textSchema("lg")),
    goodwillInteriorPaintOn: makeOptional(z.boolean()),
    goodwillInteriorPaint: makeOptional(selectSchema(workScopeStatusOptions)),
    goodwillInteriorPaintClaimedBy: claimedBySchema.optional(),
})


export const installInteriorDoorsInfoFormSchema = z.object({
    installInteriorDoors: makeOptional(selectSchema(workScopeStatusOptions)), 
    installInteriorDoorsDateCompleted: completionDateSchema,
    doorsToReplace: makeOptional(z.number(), 'number'),
    doorsNeedTrim: makeOptional(z.number(), 'number'),
    linearFeetOfCasings: makeOptional(z.number(), 'number'),
    claimedBy: claimedBySchema,
    description: makeOptional(textSchema("lg")),
    goodwillInstallInteriorDoorsOn: makeOptional(z.boolean()),
    goodwillInstallInteriorDoors: makeOptional(selectSchema(workScopeStatusOptions)),
    goodwillInstallInteriorDoorsClaimedBy: claimedBySchema.optional(),
})

export const trimInstallInfoFormSchema = z.object({
    trimInstall: makeOptional(selectSchema(workScopeStatusOptions)),
    trimInstallDateCompleted: completionDateSchema,
    linearFeetOfBaseboard: makeOptional(z.number(), 'number'),
    linearFeetOfWindowCasings: makeOptional(z.number(), 'number'),
    claimedBy: claimedBySchema,
    description: makeOptional(textSchema("lg")),
    goodwillTrimInstallOn: makeOptional(z.boolean()),
    goodwillTrimInstall: makeOptional(selectSchema(workScopeStatusOptions)),
    goodwillTrimInstallClaimedBy: claimedBySchema.optional(),
})

export const flooringInstallInfoFormSchema = z.object({
    flooringInstall: makeOptional(selectSchema(workScopeStatusOptions)),
    flooringInstallDateCompleted: completionDateSchema,
    subfloorType: makeOptional(selectSchema(whatTypeOfFlooringOptions)),
    flooringSquareFeet: makeOptional(z.number(), 'number'),
    claimedBy: claimedBySchema,
    description: makeOptional(textSchema("lg")),
    goodwillFlooringInstallOn: makeOptional(z.boolean()),
    goodwillFlooringInstall: makeOptional(selectSchema(workScopeStatusOptions)),
    goodwillFlooringInstallClaimedBy: claimedBySchema.optional(),
})

export const homeForm2Schema = z.object({
    muckGutInfo: muckGutInfoFormSchema.nullable(),
    moldRemediationInfo: moldRemediationInfoFormSchema.nullable(),
    rebuildPrepInfo: rebuildPrepInfoFormSchema.nullable(),
    roughCarpentryInfo: roughCarpentryInfoFormSchema.nullable(),
    electricalInfo: electricalInfoFormSchema.nullable(),
    hvacInfo: hvacInfoFormSchema.nullable(),
    plumbingInfo: plumbingInfoFormSchema.nullable(),
    bathroomRebuildInfo: bathroomRebuildInfoFormSchema.nullable(),
    crawlSpaceInfo: crawlSpaceInfoFormSchema.nullable(),
    insulationInstallInfo: insulationInstallInfoFormSchema.nullable(),
    drywallHangingInfo: drywallHangingInfoFormSchema.nullable(),
    mudAndTapeInfo: mudAndTapeInfoFormSchema.nullable(),
    blockWallInfo: blockWallInfoFormSchema.nullable(),
    stuccoInfo: stuccoInfoFormSchema.nullable(),
    cabinetInstallInfo: cabinetInstallInfoFormSchema.nullable(),
    interiorPaintInfo: interiorPaintInfoFormSchema.nullable(),
    installInteriorDoorsInfo: installInteriorDoorsInfoFormSchema.nullable(),
    trimInstallInfo: trimInstallInfoFormSchema.nullable(),
    flooringInstallInfo: flooringInstallInfoFormSchema.nullable(),
}).partial()

export type HomeForm2Schema = z.infer<typeof homeForm2Schema>
